/**
 * Areas (technology stack / products) — workspace-scoped.
 * Used to scope capabilities per area (e.g. DevOps in Product One vs Product Two).
 */

import { getSupabaseClient } from './supabaseClient';
import { getTenantContext } from './tenantContext';
import type { Area } from '../types/capabilities';

const TABLE_AREAS = 'areas';

export async function listAreas(workspaceId: string | null): Promise<{
  data: Area[];
  error: string | null;
}> {
  try {
    const wid = workspaceId ?? getTenantContext()?.workspaceId ?? null;
    if (!wid) return { data: [], error: null };
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE_AREAS)
      .select('*')
      .eq('workspace_id', wid)
      .order('sort_order', { ascending: true })
      .order('name', { ascending: true });
    if (error) return { data: [], error: error.message };
    return { data: (data ?? []) as Area[], error: null };
  } catch (e) {
    return { data: [], error: e instanceof Error ? e.message : 'Failed to list areas' };
  }
}

export async function createArea(params: {
  workspace_id: string;
  name: string;
  description?: string | null;
  sort_order?: number;
}): Promise<{ data: Area | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(TABLE_AREAS)
      .insert({
        workspace_id: params.workspace_id,
        name: params.name.trim(),
        description: params.description?.trim() || null,
        sort_order: params.sort_order ?? 0,
      })
      .select('*')
      .single();
    if (error) return { data: null, error: error.message };
    return { data: data as Area, error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to create area' };
  }
}

export async function updateArea(
  id: string,
  updates: { name?: string; description?: string | null; sort_order?: number }
): Promise<{ data: Area | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const payload: Record<string, unknown> = {};
    if (updates.name !== undefined) payload.name = updates.name.trim();
    if (updates.description !== undefined) payload.description = updates.description?.trim() || null;
    if (updates.sort_order !== undefined) payload.sort_order = updates.sort_order;
    const { data, error } = await supabase
      .from(TABLE_AREAS)
      .update(payload)
      .eq('id', id)
      .select('*')
      .single();
    if (error) return { data: null, error: error.message };
    return { data: data as Area, error: null };
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : 'Failed to update area' };
  }
}

export async function deleteArea(id: string): Promise<{ error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { error } = await supabase.from(TABLE_AREAS).delete().eq('id', id);
    return { error: error?.message ?? null };
  } catch (e) {
    return { error: e instanceof Error ? e.message : 'Failed to delete area' };
  }
}
